import { useMemo } from "react";
import { motion } from "framer-motion";
import { X, ChartBar, Lightning, Mountains, Leaf } from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const PIE_COLORS = ["#ef4444", "#8b5cf6", "#f97316", "#38bdf8", "#eab308", "#10b981", "#6366f1", "#ec4899"];

const MAG_COLORS = {
  "2-3": "#facc15",
  "3-4": "#fb923c",
  "4-5": "#f97316",
  "5-6": "#ef4444",
  "6-7": "#dc2626",
  "7+": "#991b1b",
};

export default function AnalyticsDrawer({ open, onClose, events, earthquakes }) {
  const categoryData = useMemo(() => {
    if (!events?.length) return [];
    const cats = {};
    events.forEach((e) => {
      const cat = e.categories?.[0]?.title || "Other";
      cats[cat] = (cats[cat] || 0) + 1;
    });
    return Object.entries(cats)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [events]);

  const magDistribution = useMemo(() => {
    if (!earthquakes?.features?.length) return [];
    const bins = { "2-3": 0, "3-4": 0, "4-5": 0, "5-6": 0, "6-7": 0, "7+": 0 };
    earthquakes.features.forEach((f) => {
      const mag = f.properties?.mag || 0;
      if (mag >= 7) bins["7+"]++;
      else if (mag >= 6) bins["6-7"]++;
      else if (mag >= 5) bins["5-6"]++;
      else if (mag >= 4) bins["4-5"]++;
      else if (mag >= 3) bins["3-4"]++;
      else bins["2-3"]++;
    });
    return Object.entries(bins).map(([range, count]) => ({ range, count }));
  }, [earthquakes]);

  const recentQuakes = useMemo(() => {
    if (!earthquakes?.features?.length) return [];
    return earthquakes.features.slice(0, 8).map((f) => ({
      id: f.id,
      mag: f.properties?.mag?.toFixed(1),
      place: f.properties?.place || "Unknown",
      time: f.properties?.time ? new Date(f.properties.time).toLocaleString() : "",
    }));
  }, [earthquakes]);

  const totalQuakes = earthquakes?.metadata?.count || 0;
  const maxMag = useMemo(() => {
    if (!earthquakes?.features?.length) return 0;
    return Math.max(...earthquakes.features.map((f) => f.properties?.mag || 0));
  }, [earthquakes]);

  if (!open) return null;

  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="absolute bottom-0 left-0 right-0 z-[950] bg-stone-900/95 backdrop-blur-xl border-t border-stone-700 rounded-t-2xl"
      data-testid="analytics-drawer"
    >
      {/* Handle */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-stone-800">
        <div className="flex items-center gap-2">
          <ChartBar size={18} weight="bold" className="text-emerald-400" />
          <span className="text-sm font-heading font-medium text-white">Analytics</span>
        </div>
        <button
          onClick={onClose}
          className="text-stone-400 hover:text-white p-1 rounded-lg hover:bg-stone-800 transition-colors"
          data-testid="analytics-close-btn"
        >
          <X size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="px-6 py-4 max-h-[45vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Stats summary */}
          <div className="space-y-4">
            <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">Overview</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Lightning size={16} className="text-amber-400 mb-1" />
                <p className="text-lg font-heading font-semibold text-white">{events?.length || 0}</p>
                <p className="text-[10px] text-stone-500">Active Events</p>
              </div>
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Mountains size={16} className="text-orange-400 mb-1" />
                <p className="text-lg font-heading font-semibold text-white">{totalQuakes}</p>
                <p className="text-[10px] text-stone-500">Earthquakes (7d)</p>
              </div>
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Mountains size={16} className="text-red-400 mb-1" />
                <p className="text-lg font-heading font-semibold text-white">{maxMag.toFixed(1)}</p>
                <p className="text-[10px] text-stone-500">Max Magnitude</p>
              </div>
              <div className="bg-stone-800/60 rounded-xl p-3 border border-stone-700/50">
                <Leaf size={16} className="text-emerald-400 mb-1" />
                <p className="text-lg font-heading font-semibold text-white">{categoryData.length}</p>
                <p className="text-[10px] text-stone-500">Event Types</p>
              </div>
            </div>

            {/* Event categories pie chart */}
            {categoryData.length > 0 && (
              <div>
                <h4 className="text-[11px] text-stone-500 mb-2 font-body">Event Categories</h4>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      innerRadius={30}
                      strokeWidth={0}
                    >
                      {categoryData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: "#1c1917",
                        border: "1px solid #44403c",
                        borderRadius: "8px",
                        fontSize: "11px",
                        color: "#fff",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-1">
                  {categoryData.map((cat, i) => (
                    <span key={cat.name} className="flex items-center gap-1 text-[10px] text-stone-400">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      {cat.name} ({cat.value})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Earthquake magnitude chart */}
          <div className="space-y-4">
            <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">
              Earthquake Magnitude Distribution
            </h3>
            {magDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={magDistribution} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#292524" />
                  <XAxis
                    dataKey="range"
                    tick={{ fill: "#78716c", fontSize: 11 }}
                    axisLine={{ stroke: "#44403c" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#78716c", fontSize: 11 }}
                    axisLine={{ stroke: "#44403c" }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#1c1917",
                      border: "1px solid #44403c",
                      borderRadius: "8px",
                      fontSize: "11px",
                      color: "#fff",
                    }}
                    formatter={(value) => [`${value} earthquakes`, "Count"]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {magDistribution.map((entry) => (
                      <Cell key={entry.range} fill={MAG_COLORS[entry.range] || "#94a3b8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-48 flex items-center justify-center text-stone-600 text-sm">
                No earthquake data
              </div>
            )}
          </div>

          {/* Recent earthquakes list */}
          <div className="space-y-4">
            <h3 className="text-xs uppercase tracking-wider text-stone-400 font-body">
              Recent Earthquakes
            </h3>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
              {recentQuakes.length > 0 ? (
                recentQuakes.map((q) => (
                  <div
                    key={q.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-stone-800/40 border border-stone-800/60"
                    data-testid={`quake-item-${q.id}`}
                  >
                    <span
                      className={`text-sm font-heading font-bold w-10 text-center ${
                        parseFloat(q.mag) >= 6
                          ? "text-red-400"
                          : parseFloat(q.mag) >= 5
                          ? "text-orange-400"
                          : parseFloat(q.mag) >= 4
                          ? "text-amber-400"
                          : "text-yellow-400"
                      }`}
                    >
                      {q.mag}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-white truncate">{q.place}</p>
                      <p className="text-[10px] text-stone-500">{q.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-32 flex items-center justify-center text-stone-600 text-sm">
                  No earthquake data
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
